import { useEffect, useState, useCallback } from "react";
import {
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiX,
  FiCopy,
  FiUser,
  FiClock,
} from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { makeRequestAuth } from "../../../../../_config/api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../utils/toastUtils";

function AllInr() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(null);

  // Rejection Modal State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const fetchTransactions = useCallback(
    async (page = 1) => {
      setIsLoading(true);
      try {
        const response = await makeRequestAuth(
          "get",
          `/admin/getTransactionList?page=${page}&limit=10&keyWord=${searchTerm}&walletType=INR&txType=withdraw`,
        );

        if (response?.data) {
          const result = response.data;
          setTransactions(result.data || []);
          setTotal(result.total || 0);
          setTotalPages(result.pages || 1);
        }
      } catch (err) {
        showErrorToast(err.message || "Failed to fetch transactions");
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm],
  );

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchTransactions(currentPage);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [currentPage, searchTerm, fetchTransactions]);

  const handleUpdateStatus = async (transactionId, status, reason = "") => {
    setIsUpdating(transactionId);
    try {
      const response = await makeRequestAuth(
        "post",
        "/admin/updateTransactionStatus",
        {
          transactionId,
          status,
          rejectReason: reason,
        },
      );

      console.log("response", response);

      if (!response.error) {
        showSuccessToast(`Transaction ${status}ed successfully`);
        setShowRejectModal(false);
        setRejectReason("");
        fetchTransactions(currentPage);
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update status");
    } finally {
      setIsUpdating(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccessToast("Copied to clipboard!");
  };

  const getStatusStyle = (status) => {
    const styles = {
      approve: "bg-emerald-100 text-emerald-700 border-emerald-200",
      pending: "bg-amber-100 text-amber-700 border-amber-200",
      reject: "bg-rose-100 text-rose-700 border-rose-200",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  return (
    <div className="p-4 md:p-8 bg-gray-50 dark:bg-[#0f172a] min-h-screen font-sans">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              INR Withdraw
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Manage and verify incoming INR transactions.
            </p>
          </div>

          <div className="relative w-full md:w-96 group">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
            <input
              type="text"
              placeholder="Search Name, Email, or UPI ID..."
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 dark:bg-gray-800 dark:border-gray-700 dark:text-white outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        {/* Stats Summary (UX Boost) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
            <p className="text-xs font-bold text-gray-400 uppercase">
              Total Withdraw
            </p>
            <p className="text-2xl font-bold dark:text-white">{total}</p>
          </div>
        </div>

        {/* Main Table Container */}
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Transaction Ref
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider text-center">
                    Actions
                  </th>
                  <th className="py-5 px-6 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                {isLoading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan="6" className="py-8 px-6">
                        <div className="h-8 bg-gray-100 dark:bg-gray-700 rounded-lg w-full"></div>
                      </td>
                    </tr>
                  ))
                ) : transactions.length > 0 ? (
                  transactions.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600">
                            {item.user?.image ? (
                              <img
                                src={item.user.image}
                                alt="Transaction Proof"
                                className="rounded-full"
                              />
                            ) : (
                              <FiUser size={20} />
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 dark:text-white">
                              {item.user?.name || "Unknown"}
                            </p>
                            <p className="text-xs text-gray-500">
                              {item.user?.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2 group">
                          <span className="text-sm font-mono text-gray-600 dark:text-gray-300">
                            {item.upi || item.hash || "No Ref"}
                          </span>
                          {(item.upi || item.hash) && (
                            <button
                              onClick={() =>
                                copyToClipboard(item.upi || item.hash)
                              }
                              className="text-gray-400 hover:text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <FiCopy size={14} />
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-indigo-500 font-bold mt-1 uppercase tracking-tighter">
                          {item.walletType}
                        </p>
                      </td>
                      <td className="py-5 px-6">
                        <span className="text-lg font-black text-gray-900 dark:text-white">
                          ₹{item.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-5 px-6">
                        <span
                          className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase border ${getStatusStyle(item.status)}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="py-5 px-6">
                        {item.status === "pending" ? (
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() =>
                                handleUpdateStatus(item._id, "approve")
                              }
                              disabled={isUpdating === item._id}
                              className="p-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl transition-all shadow-lg shadow-emerald-500/20 active:scale-95 disabled:opacity-50"
                            >
                              <FiCheck size={18} />
                            </button>
                            <button
                              onClick={() => {
                                setRejectId(item._id);
                                setShowRejectModal(true);
                              }}
                              disabled={isUpdating === item._id}
                              className="p-2 bg-rose-500 hover:bg-rose-600 text-white rounded-xl transition-all shadow-lg shadow-rose-500/20 active:scale-95 disabled:opacity-50"
                            >
                              <FiX size={18} />
                            </button>
                          </div>
                        ) : (
                          <div className="text-center text-gray-400 text-xs italic">
                            {item.status}
                          </div>
                        )}
                      </td>
                      <td className="py-5 px-6">
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <FiClock size={14} />
                          {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-20 text-center">
                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4 text-gray-400">
                          <FiSearch size={24} />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No transactions found matching your criteria.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/30 dark:bg-gray-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500 font-medium">
              Page <span className="text-indigo-600">{currentPage}</span> of{" "}
              {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <FiChevronLeft /> Previous
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white flex items-center gap-2 text-sm font-bold hover:bg-indigo-700 shadow-md shadow-indigo-600/20 disabled:opacity-50"
              >
                Next <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md rounded-3xl p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Reject Transaction
            </h3>
            <p className="text-gray-500 mb-6">
              Please provide a reason for rejecting this deposit. This will be
              visible to the user.
            </p>
            <textarea
              className="w-full p-4 rounded-2xl border border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white outline-none focus:ring-4 focus:ring-rose-500/10 focus:border-rose-500 mb-6 h-32 resize-none"
              placeholder="e.g., Invalid UPI Reference, Amount Mismatch..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="flex-1 py-3 rounded-2xl font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleUpdateStatus(rejectId, "reject", rejectReason)
                }
                disabled={!rejectReason.trim() || isUpdating}
                className="flex-1 py-3 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl font-bold transition-all disabled:opacity-50 shadow-lg shadow-rose-500/20"
              >
                {isUpdating ? "Processing..." : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AllInr;
